import React from 'react';
import { 
  Clock, 
  Calendar,
  AlertCircle,
  CheckCircle,
  Circle,
  Play,
  Edit,
  Trash2,
  RefreshCw,
  Zap
} from 'lucide-react';
import { Task } from '../../lib/db';
import TaskPriorityBadge from './TaskPriorityBadge';
import TaskDependencyBadge from './TaskDependencyBadge';

interface TaskItemProps {
  task: Task;
  onEdit: () => void;
  onDelete: () => void;
  onTimeEntry: () => void;
  onReactivate?: () => void;
  onMakeLive?: () => void;
  hasActiveTimer: boolean;
  estimatedMinutes?: number;
}

function TaskItem({
  task,
  onEdit,
  onDelete,
  onTimeEntry,
  onReactivate,
  onMakeLive,
  hasActiveTimer,
  estimatedMinutes,
}: TaskItemProps) {
  const getStatusIcon = () => {
    switch (task.status) {
      case 'Complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'In Progress':
        return <Circle className="w-5 h-5 text-blue-500 fill-blue-500" />;
      case 'Failed':
      case 'Stuck':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'Complete';

  return (
    <div className="flex items-start space-x-3 py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors group">
      {/* Status Icon */}
      <div className="mt-0.5">
        {getStatusIcon()}
      </div>

      {/* Task Content */}
      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-medium ${
          task.status === 'Complete' ? 'text-gray-500 line-through' : 'text-gray-900'
        }`}>
          {task.title}
        </h4>
        
        {/* Task metadata */}
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <TaskPriorityBadge priority={task.priority} />
          <TaskDependencyBadge task={task} />
          
          {task.is_live && (
            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
              Live
            </span>
          )}
          
          {/* Badge tells the team that a bumped task will automatically reappear tomorrow. */}
          {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
            <span
              className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
              title="This task was bumped and will reappear in Focus Mode tomorrow"
            >
              Bumped · Back Tomorrow
            </span>
          )}
          
          {task.start_date && (
            <span className="flex items-center text-xs text-gray-500">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(task.start_date)}
            </span>
          )}
          
          {task.deadline && (
            <span className={`flex items-center text-xs ${
              isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'
            }`}>
              <Clock className="w-3 h-3 mr-1" />
              Due {formatDate(task.deadline)}
            </span>
          )}
          
          <span className="flex items-center text-xs text-blue-600">
            <Clock className="w-3 h-3 mr-1" />
            {estimatedMinutes && estimatedMinutes > 0 
              ? `Est. ${formatDuration(estimatedMinutes)}`
              : 'No Time Estimate'}
          </span>
        </div>

        {/* Description */}
        {task.description && (
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
            {task.description}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Timer button */}
        {task.status !== 'Complete' && (
          <button
            onClick={onTimeEntry}
            disabled={hasActiveTimer}
            className={`p-1.5 rounded transition-all ${
              hasActiveTimer
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
            }`}
            title={hasActiveTimer ? 'Timer Active' : 'Start Timer'}
          >
            <Play className="w-4 h-4" />
          </button>
        )}
        
        {/* Make Live button for non-live tasks */}
        {task.status !== 'Complete' && !task.is_live && onMakeLive && (
          <button
            onClick={onMakeLive}
            className="p-1.5 bg-green-100 text-green-600 hover:bg-green-200 rounded transition-all"
            title="Make Task Live"
          >
            <Zap className="w-4 h-4" />
          </button>
        )}
        
        {/* Reactivate button for deferred tasks */}
        {task.notes && task.notes.includes('[Deferred to Tomorrow]') && onReactivate && (
          <button
            onClick={onReactivate}
            className="p-1.5 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded transition-all"
            title="Make Live Again"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        
        {/* Edit button */}
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-600 hover:text-gray-900 transition-colors"
          title="Edit Task"
        >
          <Edit className="w-4 h-4" />
        </button>
        
        {/* Delete button */}
        <button
          onClick={onDelete}
          className="p-1.5 text-red-600 hover:text-red-700 transition-colors"
          title="Delete Task"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default TaskItem;
